import type { RouteLocationRaw } from 'vue-router'

const supersededProjectRouteRefs = new Set<string>()

const PROJECT_LANDING_PATH_PATTERN = /^\/projects\/[^/]+\/?$/

export function extractProjectRouteRefFromUrl(url: string | null | undefined): string | null {
  if (!url) {
    return null
  }

  const path = url.split('?')[0]?.split('#')[0] ?? url
  if (!path.startsWith('/projects/')) {
    return null
  }

  const rest = path.slice('/projects/'.length)
  const slashIndex = rest.indexOf('/')
  const ref = (slashIndex === -1 ? rest : rest.slice(0, slashIndex)).trim()
  return ref || null
}

export function markSupersededProjectRouteRef(ref: string | null | undefined) {
  const trimmed = ref?.trim()
  if (!trimmed) {
    return
  }

  supersededProjectRouteRefs.add(trimmed)
}

export function isSupersededProjectRouteRef(ref: string | null | undefined): boolean {
  const trimmed = ref?.trim()
  if (!trimmed) {
    return false
  }

  return supersededProjectRouteRefs.has(trimmed)
}

export function clearSupersededProjectRouteRefs() {
  supersededProjectRouteRefs.clear()
}

export function shouldSkipBackToSupersededProjectRoute(backUrl: string | null | undefined): boolean {
  const backRef = extractProjectRouteRefFromUrl(backUrl)
  return isSupersededProjectRouteRef(backRef)
}

export function isProjectLandingPath(url: string | null | undefined): boolean {
  if (!url) {
    return false
  }

  const path = url.split('?')[0]?.split('#')[0] ?? url
  return PROJECT_LANDING_PATH_PATTERN.test(path)
}

export function isProjectWorkspaceRouteLocation(route: RouteLocationRaw): boolean {
  if (typeof route === 'string') {
    return /\/projects\/[^/]+\/workspace(?:\/|\?|$)/.test(route.split('#')[0] ?? route)
  }

  if ('path' in route && typeof route.path === 'string') {
    return /\/projects\/[^/]+\/workspace(?:\/|\?|$)/.test(route.path)
  }

  return route.name === 'projects-workspace-page'
}

export function resolveSupersededProjectBackFallback(
  fallbackRoute: RouteLocationRaw,
  currentUrl: string | null | undefined,
  projectsListRoute: RouteLocationRaw,
): RouteLocationRaw {
  if (!isProjectLandingPath(currentUrl)) {
    return fallbackRoute
  }

  if (isProjectWorkspaceRouteLocation(fallbackRoute)) {
    return projectsListRoute
  }

  return fallbackRoute
}
